import { normalizeRefText } from "./named-ref-slot.js";

/**
 * Bộ tách TỪ tiếng Việt cho các phép so theo từ của `enum-slot.ts` (chốt phủ định · đồng nghĩa · lưới câu H02 · khuôn H08(a) · chốt tên
 * PCD-HARDEN). Tách khỏi `enum-slot.ts` nguyên văn (US-AIV2-0112 PCD-HARDEN, trần 800 dòng) — không đổi luật.
 */

/** Dạng chuẩn hoá để SO: bỏ dấu (gồm `đ`→`d`, `normalizeViText`), `_`/`-` ≡ khoảng trắng, gộp khoảng trắng. */
export function normalizeEnumKey(value: string): string {
  return normalizeRefText(value.replace(/[_-]/gu, " "));
}

/**
 * Một từ của câu: chữ CÒN DẤU (thường, NFC) + dạng bỏ dấu để so (cùng phép chuẩn hoá với lựa chọn: `normalizeEnumKey`) + vị trí trong chuỗi NFC
 * + (review #1070 H1) "hình" chữ (nền + dấu mũ/móc/trăng, `đ`, KHÔNG dấu thanh) và DẤU THANH tách riêng — để so "hoà" ≡ "hòa" (dấu thanh đặt
 * kiểu cũ/mới) mà "bán" ≠ "bạn", "lốp" ≠ "lớp", "hàng" ≠ "hằng".
 */
export interface Word {
  readonly accented: string;
  readonly key: string;
  readonly start: number;
  readonly end: number;
  /** Gõ KHÔNG dấu nào (kể cả không `đ`): chữ này không phân biệt được các chữ cùng mặt bỏ dấu. */
  readonly plain: boolean;
  readonly shape: string;
  readonly tone: string;
}

/** Năm dấu thanh tiếng Việt (tổ hợp, NFD): huyền · sắc · ngã · hỏi · nặng. Dấu mũ/móc/trăng là HÌNH chữ, không phải dấu thanh. */
const TONE_MARKS_RE = /[\u0300\u0301\u0303\u0309\u0323]/gu;

function wordOf(accented: string, start: number, end: number): Word {
  const key = normalizeEnumKey(accented);
  const nfd = accented.normalize("NFD");
  return {
    accented,
    key,
    start,
    end,
    plain: accented === key,
    shape: nfd.replace(TONE_MARKS_RE, "").normalize("NFC"),
    tone: (nfd.match(TONE_MARKS_RE) ?? []).sort().join(""),
  };
}

/**
 * Tách chữ thành từ (dãy chữ/số liền nhau — `_`/`-`/dấu câu là ranh giới, cùng phép tách của `normalizeRefText`), giữ song song chữ còn dấu,
 * dạng bỏ dấu và VỊ TRÍ trên chuỗi đã NFC. Một bộ tách cho mọi phép so theo từ của `enum-slot.ts` (chốt phủ định · đồng nghĩa · lưới câu H02).
 */
export function wordsOf(text: string): Word[] {
  const out: Word[] = [];
  for (const m of text.normalize("NFC").matchAll(/[\p{L}\p{N}]+/gu)) {
    const w = wordOf(m[0].toLowerCase(), m.index, m.index + m[0].length);
    if (w.key !== "") out.push(w);
  }
  return out;
}

/**
 * 🔴 review #1070 H1 (@UC0220 @UC0192) — HAI TỪ LÀ MỘT CHỮ khi: cùng dạng bỏ dấu, VÀ — nếu CẢ HAI phía đều có dấu — cùng hình chữ và cùng dấu
 * thanh (vị trí đặt dấu thanh không tính: "hoà" ≡ "hòa"). Chỉ nới khi MỘT phía gõ không dấu ("nhom ban" khớp "nhóm bạn"). Bản đầu so bỏ dấu +
 * một cặp chép tay (quỹ/quý) ⇒ reviewer đo "nhóm bán hàng" ⇒ bạn bè, "quỹ lốp xe" ⇒ phụ huynh (develop HỎI) — luật nay là luật của CHỮ, không
 * phải danh sách cặp. Hệ quả: cách viết khác dấu của một lựa chọn ("hàng tháng" cho "hằng tháng") phải KHAI trong `synonyms` của Sổ.
 */
export function sameWord(a: Word, b: Word): boolean {
  if (a.key !== b.key) return false;
  if (a.plain || b.plain) return true;
  return a.shape === b.shape && a.tone === b.tone;
}

/**
 * 🔴 AI-REDUNDANT-ASK (@UC0199) — so một từ của CÂU với một từ của TẬP CHỮ viết ĐÚNG chính tả (tập chữ không-tên, trợ từ…). Khác `sameWord`: chữ của
 * tập có thể vốn KHÔNG dấu ("bao" của "bao giờ", "ai", "ta", "nha") — `sameWord` coi đó là "gõ thiếu dấu" ⇒ nới cho MỌI chữ cùng mặt ⇒ "Bảo" (Gia Bảo)
 * thành "bao", "nhà" thành "nha" (probe 11/10: "anh nhận được tiền của nhà bé Gia Bảo rồi" hỏi lại). Ở đây chỉ nới khi chữ CỦA CÂU gõ không dấu; chữ câu
 * CÓ dấu phải trùng hình + dấu thanh. Gõ không dấu "bao" vẫn khớp "bao" (không phân biệt được ⇒ không đoán).
 */
export function sameListedWord(sentenceWord: Word, listed: Word): boolean {
  return sentenceWord.plain ? sentenceWord.key === listed.key : sentenceWord.shape === listed.shape && sentenceWord.tone === listed.tone;
}
